import { useCallback, useEffect, useMemo } from 'react';
import { Subject, map, switchMap, takeUntil, timer } from 'rxjs';

export function useLongPress<T>(onLongPress: (payload: T) => void, delayMs: number) {
    const longPressStart$ = useMemo(() => new Subject<T>(), []);
    const longPressCancel$ = useMemo(() => new Subject<void>(), []);

    useEffect(() => {
        const subscription = longPressStart$
            .pipe(
                switchMap((payload) =>
                    timer(delayMs).pipe(
                        map(() => payload),
                        takeUntil(longPressCancel$)
                    )
                )
            )
            .subscribe(onLongPress);

        return () => subscription.unsubscribe();
    }, [delayMs, longPressCancel$, longPressStart$, onLongPress]);

    const startLongPress = useCallback(
        (payload: T) => {
            longPressStart$.next(payload);
        },
        [longPressStart$]
    );

    const cancelLongPress = useCallback(() => {
        longPressCancel$.next();
    }, [longPressCancel$]);

    return { startLongPress, cancelLongPress };
}
